import { escapeCharacters } from './string.util';

export function searchAndWrapPhrase(text: string, phraseToSearch: string, spanClass: string, allOccurrences = false) {
  return text.replace(
    new RegExp(escapeCharacters(phraseToSearch, '.*+?^${}()|[]\\'), allOccurrences ? 'gi' : 'i'),
    `<span class="${spanClass}">$&</span>`
  );
}

export function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#039;';
      default:  return c;
    }
  });
}

/**
 * @deprecated Legacy pre-conversion of an AI answer to HTML. The core AI views now keep the raw
 * markdown and render it with the `markdownRendering` pipe, which escapes any HTML it is given;
 * feeding it this output shows the tags literally. Only the Outlook add-in still calls this.
 */
export function sanitizeMarkdownToHtml(rawText: string): string {
  if (!rawText) return '';

  let html = rawText
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g,
      `<a href="$2" target="_blank" rel="noopener">$1</a>`);
  return `<p>${html}</p>`;
}

const HTML_ALLOWED_TAGS = new Set([
  'P',
  'BR',
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'STRIKE',
  'DEL',
  'INS',
  'SUB',
  'SUP',
  'CODE',
  'PRE',
  'BLOCKQUOTE',
  'UL',
  'OL',
  'LI',
  'A',
  'SPAN',
  'DIV',
  'IMG',
  'HR',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'TABLE',
  'CAPTION',
  'COLGROUP',
  'COL',
  'THEAD',
  'TBODY',
  'TFOOT',
  'TR',
  'TD',
  'TH',
]);
const HTML_DROPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED']);
const HTML_GLOBAL_ATTRIBUTES = new Set(['dir', 'lang', 'title']);
const HTML_ALLOWED_ATTRIBUTES: Record<string, ReadonlySet<string>> = {
  A: new Set(['href', 'target']),
  IMG: new Set(['src', 'alt', 'width', 'height']),
  OL: new Set(['start', 'type']),
  TABLE: new Set(['width', 'border', 'cellpadding', 'cellspacing', 'align']),
  COLGROUP: new Set(['span', 'width']),
  COL: new Set(['span', 'width']),
  TR: new Set(['align', 'valign']),
  TD: new Set(['colspan', 'rowspan', 'width', 'height', 'align', 'valign', 'headers', 'scope']),
  TH: new Set(['colspan', 'rowspan', 'width', 'height', 'align', 'valign', 'headers', 'scope']),
};
const FONT_SIZES: Record<string, string> = {
  '1': '10px',
  '2': '13px',
  '3': '16px',
  '4': '18px',
  '5': '24px',
  '6': '32px',
  '7': '48px',
};
const SAFE_LINK_SCHEMES = new Set(['http', 'https', 'mailto', 'tel']);
const SAFE_IMAGE_SCHEMES = new Set(['http', 'https', 'cid']);
const UNSAFE_STYLE_PROPERTIES = new Set(['position', 'behavior', '-moz-binding']);
const UNSAFE_STYLE_VALUE = /expression\s*\(|javascript:|vbscript:/i;
const PARAGRAPH_BLOCKS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const LINE_BLOCKS = new Set([
  'DIV',
  'UL',
  'OL',
  'LI',
  'BLOCKQUOTE',
  'PRE',
  'HR',
  'TABLE',
  'CAPTION',
  'TR',
  'DL',
  'DT',
  'DD',
  'ADDRESS',
  'FIGURE',
  'FIGCAPTION',
  'SECTION',
  'ARTICLE',
  'HEADER',
  'FOOTER',
]);

type PlainTextPart = string | number | { marker: string };

export function richHtmlToPlainText(html: string): string {
  const parts: PlainTextPart[] = [];
  collectPlainText(new DOMParser().parseFromString(html, 'text/html').body, false, parts);
  let text = '';
  let breaks = 0;
  let marker = '';
  for (const part of parts) {
    if (typeof part === 'number') {
      breaks = Math.max(breaks, part);
    } else if (typeof part !== 'string') {
      marker = part.marker;
    } else if (part !== '' && ((breaks === 0 && text !== '') || !/^[ \t\n\r\f]*$/.test(part))) {
      text += (text === '' ? '' : '\n'.repeat(breaks)) + marker + part;
      breaks = 0;
      marker = '';
    }
  }
  return text
    .split('\n')
    .map(trimSpacesAndTabs)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function collectPlainText(node: Node, preformatted: boolean, parts: PlainTextPart[]): void {
  for (const child of Array.from(node.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      const value = child.textContent ?? '';
      parts.push(preformatted ? value : value.replace(/[ \t\n\r\f]+/g, ' '));
      continue;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) {
      continue;
    }
    const element = child as Element;
    const tag = element.tagName.toUpperCase();
    if (tag === 'BR') {
      parts.push('\n');
      continue;
    }
    collectElementText(element, tag, preformatted, parts);
  }
}

function collectElementText(element: Element, tag: string, preformatted: boolean, parts: PlainTextPart[]): void {
  const breaks = blockBreaks(element, tag);
  parts.push(breaks);
  if (tag === 'LI') {
    parts.push({ marker: listMarker(element) });
  }
  collectPlainText(element, preformatted || tag === 'PRE', parts);
  if (tag === 'A') {
    parts.push(linkSuffix(element));
  }
  if ((tag === 'TD' || tag === 'TH') && element.nextElementSibling) {
    parts.push('\t');
  }
  if (tag === 'LI') {
    parts.push({ marker: '' });
  }
  parts.push(breaks);
}

function blockBreaks(element: Element, tag: string): number {
  if (PARAGRAPH_BLOCKS.has(tag)) {
    return element.parentElement?.tagName === 'LI' ? 1 : 2;
  }
  return LINE_BLOCKS.has(tag) ? 1 : 0;
}

function listMarker(item: Element): string {
  const list = item.parentElement;
  if (list?.tagName !== 'OL') {
    return '• ';
  }
  const index = Array.from(list.children)
    .filter((child) => child.tagName === 'LI')
    .indexOf(item);
  return `${index + (list as HTMLOListElement).start}. `;
}

function linkSuffix(anchor: Element): string {
  const href = anchor.getAttribute('href')?.trim() ?? '';
  const label = (anchor.textContent ?? '').trim();
  const target = href.replace(/^mailto:/i, '');
  if (href === '' || href.startsWith('unsafe:') || target.replace(/\/$/, '') === label.replace(/\/$/, '')) {
    return '';
  }
  return ` (${href})`;
}

function trimSpacesAndTabs(line: string): string {
  const isSpaceOrTab = (index: number) => line[index] === ' ' || line[index] === '\t';
  let start = 0;
  let end = line.length;
  while (start < end && isSpaceOrTab(start)) {
    start++;
  }
  while (end > start && isSpaceOrTab(end - 1)) {
    end--;
  }
  return line.slice(start, end);
}

function compactUrl(url: string): string {
  return Array.from(url)
    .filter((char) => char > ' ')
    .join('');
}

function isSafeUrl(url: string, schemes: Set<string>): boolean {
  const compact = compactUrl(url);
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(compact)?.[1].toLowerCase();
  return !compact.includes('\\') && (scheme === undefined || schemes.has(scheme));
}

export function isSafeImageSource(src: string): boolean {
  return src.trim() !== '' && (isSafeUrl(src, SAFE_IMAGE_SCHEMES) || /^data:image\//i.test(compactUrl(src)));
}

export function sanitizeRichHtml(html: string): string {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  sanitizeChildren(body);
  return body.innerHTML;
}

function sanitizeChildren(root: Element): void {
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.remove();
      continue;
    }
    const element = node as Element;
    if (HTML_DROPPED_TAGS.has(element.tagName.toUpperCase())) {
      element.remove();
      continue;
    }
    sanitizeChildren(element);
    if (element.tagName === 'FONT') {
      sanitizeAttributes(fontAsSpan(element));
      continue;
    }
    if (!HTML_ALLOWED_TAGS.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      continue;
    }
    if (element.tagName === 'IMG' && !isSafeImageSource(element.getAttribute('src') ?? '')) {
      element.remove();
      continue;
    }
    sanitizeAttributes(element);
  }
}

function fontAsSpan(font: Element): HTMLElement {
  const span = font.ownerDocument.createElement('span');
  span.style.color = font.getAttribute('color') ?? '';
  span.style.fontFamily = font.getAttribute('face') ?? '';
  span.style.fontSize = FONT_SIZES[font.getAttribute('size')?.trim() ?? ''] ?? '';
  span.style.cssText += `;${font.getAttribute('style') ?? ''}`;
  span.append(...Array.from(font.childNodes));
  font.replaceWith(span);
  return span;
}

function sanitizeAttributes(element: Element): void {
  const allowed = HTML_ALLOWED_ATTRIBUTES[element.tagName];
  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase();
    if (name === 'style') {
      sanitizeStyle(element);
      continue;
    }
    if (!HTML_GLOBAL_ATTRIBUTES.has(name) && !allowed?.has(name)) {
      element.removeAttribute(attribute.name);
      continue;
    }
    if (element.tagName === 'A' && name === 'href') {
      sanitizeHref(element, attribute.value);
    }
  }
}

function sanitizeHref(anchor: Element, href: string): void {
  if (!isSafeUrl(href, SAFE_LINK_SCHEMES)) {
    anchor.removeAttribute('href');
    return;
  }
  if (/^www\./i.test(href.trim())) {
    anchor.setAttribute('href', `https://${href.trim()}`);
  }
}

function sanitizeStyle(element: Element): void {
  const style = (element as HTMLElement).style;
  if (!style) {
    element.removeAttribute('style');
    return;
  }
  for (const property of Array.from(style)) {
    if (
      UNSAFE_STYLE_PROPERTIES.has(property) ||
      UNSAFE_STYLE_VALUE.test(style.getPropertyValue(property))
    ) {
      style.removeProperty(property);
    }
  }
  if (style.length === 0) {
    element.removeAttribute('style');
  }
}
